import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiUser, FiLock, FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { authAPI } from '../api/endpoints';
import { setUser } from '../utils/helpers';
import logo from '../assets/images/logo.png';
import {
  LoginContainer,
  LoginCard,
  LoginLeft,
  LoginRight,
  Logo,
  LoginTitle,
  LoginSubtitle,
  LoginForm,
  ForgotPassword,
  Divider,
  SocialLogin,
  SocialButton,
  SignupPrompt,
  ThemeToggle,
  BackgroundShape,
  FeatureList,
  FeatureItem,
  FeatureIcon,
} from './Login.styles';

const Login = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      const response = await authAPI.login(formData);
      const { user } = response.data;

      // Store user data (token is handled by cookies)
      setUser(user);

      // Navigate based on user role
      const roleRoutes = {
        student: '/learner',
        parent: '/parent',
        teacher: '/teacher',
        publisher: '/publisher',
        admin: '/admin',
        owner: '/owner',
      };

      navigate(roleRoutes[user.role] || '/learner');
    } catch (error) {
      setErrors({
        general: error.response?.data?.error || 'Login failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <BackgroundShape className="shape-1" />
      <BackgroundShape className="shape-2" />
      <BackgroundShape className="shape-3" />

      <ThemeToggle onClick={toggleTheme}>
        {isDarkMode ? <FiSun /> : <FiMoon />}
      </ThemeToggle>

      <LoginCard>
        <LoginLeft>
          <Logo style={{ justifyContent: 'center' }}>
            <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
          </Logo>

          <FeatureList>
            <FeatureItem>
              <FeatureIcon>🎓</FeatureIcon>
              <div>
                <h4>Quality Education</h4>
                <p>Access CBC-aligned content from expert teachers</p>
              </div>
            </FeatureItem>

            <FeatureItem>
              <FeatureIcon>📱</FeatureIcon>
              <div>
                <h4>Learn Anywhere</h4>
                <p>Mobile-friendly platform for learning on the go</p>
              </div>
            </FeatureItem>

            <FeatureItem>
              <FeatureIcon>📊</FeatureIcon>
              <div>
                <h4>Track Progress</h4>
                <p>Monitor performance with detailed analytics</p>
              </div>
            </FeatureItem>

            <FeatureItem>
              <FeatureIcon>👨‍👩‍👧</FeatureIcon>
              <div>
                <h4>Parent Dashboard</h4>
                <p>Stay connected with your child's learning journey</p>
              </div>
            </FeatureItem>
          </FeatureList>
        </LoginLeft>

        <LoginRight>
          <LoginTitle>Welcome Back!</LoginTitle>
          <LoginSubtitle>Sign in to continue your learning journey</LoginSubtitle>

          {errors.general && (
            <div style={{
              padding: '12px',
              backgroundColor: '#FEE2E2',
              color: '#DC2626',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}

          <LoginForm onSubmit={handleSubmit}>
            <Input
              label="Username"
              type="text"
              name="username"
              placeholder="Enter your username"
              value={formData.username}
              onChange={handleChange}
              error={errors.username}
              leftIcon={<FiUser />}
              required
            />

            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              leftIcon={<FiLock />}
              required
            />

            <Link to="/forgot-password" style={{
              fontSize: '14px',
              color: '#FFFFFF',
              textAlign: 'right',
              marginTop: '-8px',
              textDecoration: 'none'
            }}>
              Forgot Password?
            </Link>

            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
            >
              Sign In
            </Button>
          </LoginForm>

          <Divider>
            <span>or continue with</span>
          </Divider>

          <SocialLogin>
            <SocialButton>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Google
            </SocialButton>

            <SocialButton>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="currentColor" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              Facebook
            </SocialButton>
          </SocialLogin>

          <SignupPrompt>
            Don't have an account? <Link to="/register">Sign up</Link>
          </SignupPrompt>
        </LoginRight>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;

